// Balises `<head>` de la page courante (Phase 96, §Q3 / D-09).
//
// ⚠️ CE COMPOSANT SE MONTE À UN SEUL ENDROIT — `AppContent`, à côté de
// `ScrollToHash`. React ne DÉDUPLIQUE PAS les `<link>` : deux instances
// produisent deux `<link rel="canonical">`, même avec un `href` identique,
// donc deux signaux contradictoires envoyés aux moteurs, sans la moindre
// erreur visible ni en développement ni en production (T-96-13). Le besoin de
// balises propres à une page se satisfait en ajoutant une ligne à
// `routes.ts`, jamais en montant un second `SeoHead`.
//
// Aucun effet de bord, aucune insertion manuelle dans le document : React 19
// remonte lui-même les balises rendues en JSX, les REMPLACE à la navigation et
// les RETIRE au démontage. C'est ce troisième comportement qu'une insertion
// maison oublie, et il est exactement celui qui empêche un `noindex` de rester
// collé à la page suivante. Ce composant est donc une fonction de rendu pure —
// rien à synchroniser après coup.
//
// Le `<title>` de `BlogArticle.tsx` reste où il est : il est dynamique (il
// vient de la base) et n'entre en conflit avec rien d'ici.
// Refs: phase 96.
import { useLocation } from 'react-router-dom'
import { useLanguage } from '../i18n/LanguageProvider'
import { seoLinks } from './seoLinks'
import type { PublicRoute } from './routes'

export default function SeoHead({
  /**
   * Table de routes dont dériver les balises. Défaut : `PUBLIC_ROUTES`.
   *
   * CETTE PROP EXISTE POUR LES TESTS, et n'est JAMAIS passée en production —
   * `AppContent` monte `<SeoHead />` nu. Elle permet aux quatre témoins SEO de
   * garder un sujet `indexable && !translated` quand la table de production
   * n'en porte plus (D-04, phase 99). Elle ne desserre EN RIEN l'invariant
   * « un seul montage » de l'en-tête ci-dessus : deux montages restent deux
   * canonical, quelle que soit la table.
   */
  routes,
}: {
  routes?: readonly PublicRoute[]
}) {
  // `pathname` est NU : le `basename` du routeur a déjà retiré le préfixe `/en`.
  const { pathname } = useLocation()
  const { lang } = useLanguage()
  const { canonical, alternates, noindex } = seoLinks(pathname, lang, routes)

  return (
    <>
      <link rel="canonical" href={canonical} />
      <meta property="og:url" content={canonical} />
      {noindex && <meta name="robots" content="noindex" />}
      {/* La prop JSX s'écrit `hrefLang` et produit bien l'attribut `hreflang`. */}
      {alternates.map((alt) => (
        <link key={alt.hreflang} rel="alternate" hrefLang={alt.hreflang} href={alt.href} />
      ))}
    </>
  )
}
